// src/components/NavBar.js
/* ------------------------------------------------------------------ */
/* NavBar – responsive top-level navigation                            */
/* ------------------------------------------------------------------ */
/**
 * Behaviour
 * ----------
 * • **Mobile (< md)** – hamburger toggles a slide-down, full-height overlay
 *   containing links: Home · Assessment · Questionnaires · Results · Sign out.
 * • **Desktop (≥ md)** – keeps inline links and icons.
 *
 * @param {object}  props
 * @param {string=} props.className  Extra Tailwind classes for the `<header>`.
 * @returns {JSX.Element}
 */

import React, { useState, useCallback } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { getAuth, signOut } from "firebase/auth";
import { firebaseApp } from "../../firebase/firebaseApp";
import { HomeIcon } from "../icons/HomeIcon";
import { LogoutIcon } from "../icons/LogoutIcon";
import { CloseIcon } from "../icons/CloseIcon";
import { HamburgerIcon } from "../icons/HamburgerIcon";
import { useTranslation } from "react-i18next";
import LangSwitcher from "../LangSwitcher";
import { useUserEmail } from "../../hooks/useUserEmail";
import { emailInitial } from "../../utils/email";

/* ------------------------------------------------------------------ */
/* Component                                                          */
/* ------------------------------------------------------------------ */
function NavBar({ className = "" }) {
  const { t } = useTranslation();

  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { email: userEmail, loading: loadingEmail } = useUserEmail();

  /** Sign user out and redirect to `/auth/login` */
  const handleSignOut = useCallback(() => {
    signOut(getAuth(firebaseApp))
      .then(() => navigate("/", { replace: true }))
      .catch(
        (err) => process.env.NODE_ENV === "development" && console.error(err)
      );
  }, [navigate]);

  /** Close the slide-down menu after a navigation click */
  const handleNavigate = useCallback(() => setMenuOpen(false), []);

  const items = [
    { to: "/home", icon: "home", label: "Home" },
    { to: "/recording", icon: "radio_button_checked", label: "Record" },
    { to: "/settings", icon: "settings", label: "Settings" },
  ];

  const base =
    "flex flex-col items-center justify-center gap-1 py-2 min-w-0 text-nav-title";

  return (
    <>
      {/* ── Top bar ─────────────────────────────────────────────────── */}
      <header
        className={`flex items-center justify-between w-full p-2 md:gap-4 z-50 ${className} bg-container`}
      >
        {/* Logo */}
        <Link
          className="flex flex-col h-fit"
          to="/home"
          aria-label="BreatheUlra Home"
          onClick={handleNavigate}
        >
          <img
            src="/logos/BreatheUltra_Main_White_Logo.png"
            alt="PDX logo"
            className="h-6 w-auto"
            loading="lazy"
          />
        </Link>

        <ul className="grid-cols-3 hidden md:grid h-fit gap-8">
          {items.map(({ to, icon, label }) => (
            <li key={to} className="contents">
              <NavLink
                to={to}
                className={({ isActive }) =>
                  [base, isActive ? "text-primary" : "text-greyscale-1"].join(
                    " "
                  )
                }
                aria-label={label}
              >
                {label}
              </NavLink>
            </li>
          ))}
        </ul>

        {/* Desktop icons and links */}
        <nav className="flex items-center gap-4">
          <div className="hidden md:flex">
            <LangSwitcher />
          </div>

          <NavLink
            to={"/settings"}
            className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold"
          >
            {emailInitial(userEmail)}
          </NavLink>
        </nav>
      </header>

      {/* ── Slide-down keyframe ────────────────────────────────────── */}
      <style>{`
        @keyframes slideDown {
          from { transform: translateY(-100%); }
          to   { transform: translateY(0); }
        }
      `}</style>
    </>
  );
}

export default React.memo(NavBar);
